import React, { useState, useEffect } from 'react';
import {
  optimizationService,
  type OverviewData,
  type MonthlyInsightsData,
  type SnapshotMeta,
} from '../../../services/optimizationService';

interface OptimizationOverviewProps {
  onTooltipShow: (title: string, content: string) => void;
  onNavigate: (section: string) => void;
}

export default function OptimizationOverview({ onTooltipShow, onNavigate }: OptimizationOverviewProps) {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [insights, setInsights] = useState<MonthlyInsightsData | null>(null);
  const [meta, setMeta] = useState<SnapshotMeta | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, insightsRes] = await Promise.all([
        optimizationService.getOverview(),
        optimizationService.getMonthlyInsights(selectedMonth || undefined),
      ]);

      if (overviewRes.status === 'success' && overviewRes.data) {
        setOverview(overviewRes.data);
      }
      if (overviewRes.meta) {
        setMeta(overviewRes.meta);
      }

      if (insightsRes.status === 'success' && insightsRes.data) {
        setInsights(insightsRes.data);
        if (!selectedMonth && insightsRes.data.month) {
          setSelectedMonth(insightsRes.data.month);
        }
      }
    } catch (err) {
      console.error('Failed to load optimization overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSnapshot = async () => {
    setRefreshing(true);
    setRefreshMessage('Triggering snapshot recalculation...');
    try {
      const res = await optimizationService.triggerSnapshot(selectedMonth || undefined);
      setRefreshMessage(res.message || 'Snapshot triggered successfully. Updating view...');
      setTimeout(() => {
        fetchData();
        setRefreshing(false);
        setRefreshMessage(null);
      }, 2000);
    } catch (err: any) {
      console.error('Failed to trigger snapshot:', err);
      setRefreshMessage(err?.message || 'Failed to trigger snapshot refresh.');
      setTimeout(() => {
        setRefreshing(false);
        setRefreshMessage(null);
      }, 3000);
    }
  };

  // Format month display (e.g., "Oct 2026")
  const formatMonthDisplay = (monthStr: string) => {
    if (!monthStr) {
      return new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    try {
      const parts = monthStr.split('-');
      if (parts.length >= 2) {
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1);
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    } catch {
      // fallback
    }
    return monthStr;
  };

  const currentMonthFormatted = formatMonthDisplay(selectedMonth || insights?.month || '');

  // Extract counts with fallback to tiles or snapshot defaults
  const getTileCount = (key: string) => insights?.tiles?.find((t) => t.key === key)?.count;
  const declinesCount = overview?.ecosystem?.declines?.count ?? getTileCount('declines') ?? (loading ? '—' : 4);
  const competitorsCount = overview?.ecosystem?.competitors?.count ?? getTileCount('competitors') ?? (loading ? '—' : 4);
  const opportunitiesCount = overview?.ecosystem?.opportunities?.count ?? getTileCount('opportunities') ?? (loading ? '—' : 5);
  const updatesCount = overview?.ecosystem?.updates?.count ?? getTileCount('updates') ?? (loading ? '—' : 2);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* 1. Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            Optimization Overview
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {currentMonthFormatted} · Monthly Refreshed Data
            {meta?.generated_at && (
              <span className="ml-2 text-xs text-gray-400">
                (Last calculated: {new Date(meta.generated_at).toLocaleDateString()})
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Historical Month Dropdown if available */}
          {insights?.available_months && insights.available_months.length > 1 && (
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            >
              {insights.available_months.map((m) => (
                <option key={m} value={m}>
                  {formatMonthDisplay(m)} Snapshot
                </option>
              ))}
            </select>
          )}

          <button
            onClick={() => onTooltipShow('Optimization Overview', 'The Optimization section provides a monthly synthesized view of external ranking declines, competitor moves, high-ROI keyword opportunities, and Google algorithm impact.')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <i className="ri-information-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Refresh feedback toast/bar if triggered */}
      {refreshMessage && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl text-xs text-blue-800 dark:text-blue-300 flex items-center space-x-2 animate-fadeIn">
          <i className="ri-loader-4-line animate-spin text-blue-600"></i>
          <span>{refreshMessage}</span>
        </div>
      )}

      {/* 2. Video Tutorial Bar (Greyed out / Disabled as per requirements) */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-200 dark:border-gray-700 shadow-sm opacity-60 cursor-not-allowed">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3.5">
            <div className="w-11 h-11 bg-purple-600/90 dark:bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white shadow-sm">
              <i className="ri-play-fill text-xl ml-0.5"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                How Optimization Overview Works
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                3–4 min tutorial · Video Coming Soon
              </p>
            </div>
          </div>

          <button
            disabled
            className="px-4 py-2 bg-emerald-600/50 dark:bg-emerald-600/40 text-white/70 rounded-xl text-xs font-semibold cursor-not-allowed flex items-center space-x-2 whitespace-nowrap self-start sm:self-auto"
          >
            <i className="ri-play-line"></i>
            <span>Watch Demo</span>
          </button>
        </div>
      </div>

      {/* 3. Main Ecosystem Monitor Deep Dive Card */}
      <div
        onClick={() => onNavigate('ecosystem-monitor')}
        className="group relative bg-gradient-to-br from-orange-50/80 via-red-50/40 to-white dark:from-gray-800 dark:to-gray-800/80 rounded-2xl p-6 lg:p-8 border border-orange-200 dark:border-gray-700 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-4 max-w-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white text-xl shadow-md">
                <i className="ri-radar-line"></i>
              </div>
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-orange-600 dark:text-orange-400 block">
                  DEEP DIVE SECTION
                </span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Ecosystem Monitor
                </h3>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              Monitor SERP ranking declines with 3-pillar diagnostics, track emerging competitor threat velocity, discover high-ROI keyword gaps, and analyze Google algorithm impact.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              <div className="flex items-center space-x-2 bg-white/70 dark:bg-gray-700/40 p-2.5 rounded-lg border border-orange-100 dark:border-gray-600/40">
                <i className="ri-arrow-down-circle-line text-red-500 text-sm"></i>
                <span>{declinesCount} Keywords with Performance Drops</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/70 dark:bg-gray-700/40 p-2.5 rounded-lg border border-orange-100 dark:border-gray-600/40">
                <i className="ri-user-search-line text-orange-500 text-sm"></i>
                <span>{competitorsCount} Emerging Competitor Threats</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/70 dark:bg-gray-700/40 p-2.5 rounded-lg border border-orange-100 dark:border-gray-600/40">
                <i className="ri-lightbulb-line text-emerald-500 text-sm"></i>
                <span>{opportunitiesCount} High-ROI Growth Opportunities</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/70 dark:bg-gray-700/40 p-2.5 rounded-lg border border-orange-100 dark:border-gray-600/40">
                <i className="ri-google-line text-blue-500 text-sm"></i>
                <span>{updatesCount} In-Scope Algorithm Updates</span>
              </div>
            </div>
          </div>

          <div className="lg:min-w-[240px] flex flex-col justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNavigate('ecosystem-monitor');
              }}
              className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold text-sm transition-all shadow-md hover:from-orange-600 hover:to-red-700 flex items-center justify-center space-x-2"
            >
              <span>Launch Ecosystem Monitor</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* 4. Bottom Notice: Monthly Optimization Insights (matching prototype) */}
      <div className="bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/80 dark:border-blue-900/50 rounded-2xl p-4 sm:p-5 flex items-start space-x-3 text-xs text-blue-900 dark:text-blue-200 leading-relaxed shadow-sm">
        <i className="ri-information-line text-blue-600 dark:text-blue-400 text-base flex-shrink-0 mt-0.5"></i>
        <div>
          <h4 className="font-bold text-xs sm:text-sm text-blue-950 dark:text-blue-100 mb-0.5">
            Monthly Optimization Insights
          </h4>
          <p className="text-[11px] sm:text-xs text-blue-800/90 dark:text-blue-300">
            These snapshots are refreshed monthly to provide you with the latest optimization opportunities. Click any card to explore detailed insights and take action on recommendations.
          </p>
        </div>
      </div>
    </div>
  );
}

